import { RefObject, useEffect, useRef, useState } from 'react';
import { Pause, Play, Volume2, VolumeX } from 'lucide-react';

import { cn } from '@repo/ui/lib/utils';
import { Button } from '@repo/ui/components/ui/button';
import { type CandidateMedia } from '@repo/ui/hooks';
import { type SpotlightMediaReadyState } from '../component';

type VideoProps = {
  className?: string;
  isHovered?: boolean;
  isMuted: boolean;
  autoplay: boolean;
  media: CandidateMedia;
  showPlayPauseVideoControl?: boolean;
  showVolumeControl?: boolean;
  onVolumeButtonClick?: () => void;
  onReady?: (
    data: Omit<SpotlightMediaReadyState, 'startImageMediaAnimation'>,
  ) => void;
  videoRef?: RefObject<HTMLVideoElement>;
};

export const Video = ({
  media,
  className,
  isHovered,
  isMuted,
  autoplay,
  onReady,
  showPlayPauseVideoControl,
  showVolumeControl,
  onVolumeButtonClick,
  videoRef,
}: VideoProps) => {
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const ref = videoRef || useRef<HTMLVideoElement>(null);

  const disposeVideo = (videoReference: RefObject<HTMLVideoElement>) => {
    if (!videoReference?.current) return;

    try {
      videoReference.current?.pause();
      videoReference.current?.removeAttribute('src');
      videoReference?.current?.load();
    } catch (error) {
      console.error('Error disposing video:', error);
    }
  };

  const handleVideoClick = (videoElementRef: RefObject<HTMLVideoElement>) => {
    if (!videoElementRef?.current) return;

    try {
      if (videoElementRef?.current?.paused) {
        handleVideoPlay(videoElementRef);
      } else {
        handleVideoPause(videoElementRef);
      }
    } catch (error) {
      console.error('Error clicking video:', error);
    }
  };

  const handleVideoPlay = (videoElementRef: RefObject<HTMLVideoElement>) => {
    if (!videoElementRef?.current) return;

    videoElementRef.current?.play();
    setIsPlaying(true);
  };

  const handleVideoPause = (videoElementRef: RefObject<HTMLVideoElement>) => {
    if (!videoElementRef?.current) return;

    try {
      videoElementRef.current?.pause();
      setIsPlaying(false);
    } catch (error) {
      console.error('Error pausing video:', error);
    }
  };

  useEffect(() => {
    onReady?.({
      disposeVideo: () => disposeVideo(ref),
      pauseVideo: () => handleVideoPause(ref),
    });

    return () => disposeVideo(ref);
  }, []);
  return (
    <>
      <figure className="z-10 w-full h-full relative object-contain aspect-[322/429]">
        <video
          loop={true}
          ref={ref}
          data-testid="spotlight-media-video"
          className={cn(
            'bg-black w-full h-full relative object-contain  aspect-[322/429]',
            className,
          )}
          muted={isMuted}
          autoPlay={isPlaying}
          onClick={() => handleVideoClick(ref)}
        >
          <source src={media.url} type="video/mp4" />
        </video>
      </figure>
      {showPlayPauseVideoControl && (
        <Button
          className={cn(
            'z-50 rounded-full w-8 h-8 bg-white/10  hover:bg-white/10 text-white hover:text-white p-2.5 border-transparent absolute inset-y-2/4 inset-x-2/4 backdrop-blur-2xl sm:group-hover:flex cursor-pointer!',
            {
              hidden: !isHovered,
            },
          )}
          variant="outline"
          onClick={() => handleVideoClick(ref)}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4" />
          )}
        </Button>
      )}

      {showVolumeControl && (
        <Button
          className="z-50 rounded-full w-8 h-8 bg-transparent hover:bg-transparent text-white hover:text-white p-2.5 border-white/70 absolute top-4 right-4"
          variant="outline"
          role="volume-control-button"
          onClick={onVolumeButtonClick}
        >
          {isMuted ? (
            <VolumeX className="w-4 h-4" />
          ) : (
            <Volume2 className="w-4 h-4" />
          )}
        </Button>
      )}
    </>
  );
};
